// Realtime helpers for the producer surface.
//
// Layered on top of `realtime.ts`'s generic Postgres-changes subscriber:
//   - `useProducerProjectChannel` — invalidates queries when episode rows
//     update (processing percent + state) or batch rows update.
//   - `useProducerTakesChannel`   — invalidates the review queue when new
//     pending-supervisor takes arrive.
//
// Both hooks no-op until a non-empty `orgId` is provided so they're safe to
// mount before the org context resolves.

import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useId } from 'react'
import { producerProjectKey, producerProjectsKey } from './api/producer-projects'
import { reviewQueueKey } from './api/producer-takes'
import { supabase } from './supabase'

export interface ProjectProgressPayload {
  project_id: string
  episode_id?: string
  /** New status if the project rolled forward. */
  status?: string
  processing_state?: string
  /** 0-100. */
  processing_percent?: number
}

/**
 * Subscribe to the org-scoped project_progress broadcast channel. The
 * server publishes to this channel from the Modal job webhooks; the SPA only
 * listens. On any payload we invalidate the matching project + episode keys.
 *
 * Returns nothing — the cleanup is wired through useEffect.
 */
export function useProducerProjectChannel(orgId: string | null | undefined): void {
  const qc = useQueryClient()
  // Per-instance suffix prevents collision with other subscribers on the same
  // logical topic (Supabase v2 keys channels by name; once one has subscribed
  // any sibling `.on(...)` registration throws).
  const instanceId = useId()
  useEffect(() => {
    if (!orgId) return
    const channel = supabase
      .channel(`org:${orgId}:project_progress#${instanceId}`)
      .on('broadcast', { event: 'progress' }, (msg) => {
        const payload = msg['payload'] as ProjectProgressPayload | undefined
        if (!payload) return
        void qc.invalidateQueries({ queryKey: producerProjectsKey })
        if (payload.project_id) {
          void qc.invalidateQueries({ queryKey: producerProjectKey(payload.project_id) })
        }
        if (payload.episode_id) {
          void qc.invalidateQueries({ queryKey: ['episode', payload.episode_id] })
        }
      })
      .subscribe()

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [orgId, qc, instanceId])
}

export interface TakeBroadcastPayload {
  project_id: string
  take_id: string
}

/**
 * Subscribe to the org-scoped takes broadcast channel. Used by the take review
 * route + the project dashboard's "recent take stream" widget. We don't hand
 * payloads to consumers — they re-read from the cache after the invalidate.
 */
export function useProducerTakesChannel(
  orgId: string | null | undefined,
  projectId?: string,
): void {
  const qc = useQueryClient()
  const instanceId = useId()
  useEffect(() => {
    if (!orgId) return
    const channel = supabase
      .channel(`org:${orgId}:takes#${instanceId}`)
      .on('broadcast', { event: 'take' }, (msg) => {
        const payload = msg['payload'] as TakeBroadcastPayload | undefined
        if (!payload) return
        if (projectId && payload.project_id !== projectId) return
        void qc.invalidateQueries({ queryKey: reviewQueueKey(payload.project_id) })
        void qc.invalidateQueries({ queryKey: producerProjectKey(payload.project_id) })
      })
      .subscribe()

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [orgId, projectId, qc, instanceId])
}
